import { List } from "immutable";
import { isNumber } from "lodash";
import PropTypes from "prop-types";
import React from "react";

import { ConstraintComparison, ConstraintTarget, ConstraintTypes } from "../../constants/constraints";
import { extractDateFromYearString } from "../../modules/definition/calendar/dateFunctions/dateWithString";
import { stringFromDate } from "../../modules/definition/calendar/dateFunctions/stringFromDate";
import { stringFromTimespan } from "../../modules/definition/calendar/dateFunctions/timespanFunctions";
import { getMonthsFromCalendar, getWeekdaysFromCalendar } from "../../modules/definition/calendar/selectors";
import { immutableRecords, Precision, PropTypeShapes } from "../../types";
import calendarTypes from "../../types/calendarTypes";
import preprocessorQuery from "../../utils/preprocessorQuery";
import FormButton from "../form/FormButton";
import SingleFormDatePicker from "../form/formDatePicker/SingleFormDatePicker";
// import "./relationshipPopover.scss";
// import selectors from "./selectors";
import FormDropdown from "../form/FormDropdown";
import FormDurationPicker from "../form/FormDurationPicker";
import FormList from "../form/FormList";
import FormTextField from "../form/FormTextField";
import { withPopoverRepositionContext } from "../popover/PopoverRepositionContext";
import InspectorField from "./InspectorField";
import InspectorRow from "./InspectorRow";

const targetOptions = [
  {
    label: "Start",
    value: ConstraintTarget.Start
  },
  {
    label: "End",
    value: ConstraintTarget.End
  }
];

const getComparisonOptions = (calendar, constraintDependencyOptions) => {
  const disableRelative = constraintDependencyOptions.size === 0;
  let options = [
    {
      label: "Date",
      value: "date-header",
      header: true
    },
    {
      label: "is after date",
      value: `${ConstraintTypes.Date}-${ConstraintComparison.After}`
    },
    {
      label: "is before date",
      value: `${ConstraintTypes.Date}-${ConstraintComparison.Before}`
    },
    {
      label: "equals date",
      value: `${ConstraintTypes.Date}-${ConstraintComparison.Equal}`
    },
    {
      label: "Item",
      value: "item-header",
      header: true,
      disabled: disableRelative
    },
    {
      label: "is after start of",
      value: `${ConstraintTypes.Relative}-${ConstraintComparison.After}-${ConstraintTarget.Start}`,
      disabled: disableRelative
    },
    {
      label: "is before start of",
      value: `${ConstraintTypes.Relative}-${ConstraintComparison.Before}-${ConstraintTarget.Start}`,
      disabled: disableRelative
    },
    {
      label: "equals start of",
      value: `${ConstraintTypes.Relative}-${ConstraintComparison.Equal}-${ConstraintTarget.Start}`,
      disabled: disableRelative
    },
    {
      label: "is after end of",
      value: `${ConstraintTypes.Relative}-${ConstraintComparison.After}-${ConstraintTarget.End}`,
      disabled: disableRelative
    },
    {
      label: "is before end of",
      value: `${ConstraintTypes.Relative}-${ConstraintComparison.Before}-${ConstraintTarget.End}`,
      disabled: disableRelative
    },
    {
      label: "equals end of",
      value: `${ConstraintTypes.Relative}-${ConstraintComparison.Equal}-${ConstraintTarget.End}`,
      disabled: disableRelative
    },
    {
      label: "Date Components",
      value: "date-component-header",
      header: true
    }
  ];

  if (calendar.calendarType === calendarTypes.CALENDAR_TYPE_DATE_TIME) {
    options.push(
      {
        label: "matches year",
        value: `${ConstraintTypes.DatePart}-${Precision.Year}`
      },
      {
        label: "matches month",
        value: `${ConstraintTypes.DatePart}-${Precision.Month}`
      },
      {
        label: "matches day of month",
        value: `${ConstraintTypes.DatePart}-${Precision.Day}`
      }
    );
  }

  if (
    calendar.calendarType === calendarTypes.CALENDAR_TYPE_DATE_TIME ||
    calendar.calendarType === calendarTypes.CALENDAR_TYPE_WEEKDAY_TIME
  ) {
    options.push({
      label: "matches weekday",
      value: `${ConstraintTypes.DatePart}-${Precision.Week}`
    });
  }

  options.push(
    {
      label: "matches hour",
      value: `${ConstraintTypes.DatePart}-${Precision.Hour}`
    },
    {
      label: "matches minute",
      value: `${ConstraintTypes.DatePart}-${Precision.Minute}`
    },
    {
      label: "matches second",
      value: `${ConstraintTypes.DatePart}-${Precision.Second}`
    }
  );

  return options;
};

const isValidNumericPart = (_calendar, part) => {
  // TODO: Check if day/hour/min/seconds are in a valid range
  let num = parseInt(part);
  return isNumber(num);
};

class InspectorEditDateConstraintView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      target: ConstraintTarget.Start,
      constraintType: ConstraintTypes.Date,
      comparison: ConstraintComparison.After,
      objectTarget: null,
      object: null,
      offset: null,
      date: null,
      partPrecision: null,
      partValue: null,
      ids: null
    };

    this.onTargetChange = this.onTargetChange.bind(this);
    this.onTypeChange = this.onTypeChange.bind(this);
    this.onDateChange = this.onDateChange.bind(this);
    this.onObjectChange = this.onObjectChange.bind(this);
    this.onOffsetChange = this.onOffsetChange.bind(this);
    this.onPartValueChange = this.onPartValueChange.bind(this);
    this.onAddClick = this.onAddClick.bind(this);

    const { constraint } = props;
    if (constraint) {
      this.state = {
        target: constraint.thisTarget,
        constraintType: constraint.constraintType,
        comparison: constraint.comparison,
        objectTarget: constraint.otherTarget,
        object: constraint.other,
        offset: constraint.offset,
        date: constraint.date,
        partPrecision: constraint.partPrecision,
        partValue: constraint.partValue,
        ids: constraint.ids
      };
    }
  }

  componentDidUpdate() {
    if (this.props.repositionPopover) {
      this.props.repositionPopover();
    }
  }

  onTargetChange(target) {
    this.setState({ target });
  }

  onTypeChange(value) {
    const values = value.split("-");
    const constraintType = values.length > 0 ? values[0] : null;
    if (constraintType === ConstraintTypes.Relative || constraintType === ConstraintTypes.Date) {
      const comparison = values.length > 1 ? values[1] : null;
      const objectTarget = values.length > 2 ? values[2] : null;
      this.setState({ constraintType, comparison, objectTarget });
    } else {
      const partPrecision = values.length > 1 ? Number(values[1]) : null;
      this.setState({ constraintType, partPrecision });
    }
  }

  onDateChange(date) {
    let newDate = date;
    if (newDate) {
      const label = stringFromDate(this.props.calendar, newDate);
      newDate = immutableRecords.DateDisplayRecord(newDate).set("label", label);
    }
    this.setState({
      date: newDate
    });
  }

  onObjectChange(object) {
    this.setState({ object });
  }

  onOffsetChange(offset) {
    const label = stringFromTimespan(offset);
    this.setState({
      offset: immutableRecords.TimespanDisplayRecord(offset).set("label", label)
    });
  }

  onPartValueChange(partValue) {
    // TODO: Currently messy as constraints are a comma separated string rather than array
    // Allowed values may change though as auto resolution is set up, so revisit this later ATR-1125
    if (List.isList(partValue)) {
      this.setState({ partValue: partValue.join(", ") });
    } else {
      const parts = partValue.split(",");
      const validatedParts = parts.reduce((arr, part) => {
        const rangeParts = part.split("-");
        if (rangeParts.length === 1) {
          // It's a single date
          if (this.state.partPrecision === Precision.Year) {
            if (extractDateFromYearString(this.props.calendar, part)) {
              arr.push(part);
            }
          } else if (isValidNumericPart(this.props.calendar, part)) {
            arr.push(part);
          }
        } else if (rangeParts.length === 2) {
          // It's a range like 2010-2015
          if (this.state.partPrecision === Precision.Year) {
            if (
              extractDateFromYearString(this.props.calendar, rangeParts[0]) &&
              extractDateFromYearString(this.props.calendar, rangeParts[1])
            ) {
              arr.push(part);
            }
          } else if (
            isValidNumericPart(this.props.calendar, rangeParts[0]) &&
            isValidNumericPart(this.props.calendar, rangeParts[1])
          ) {
            arr.push(part);
          }
        }
        return arr;
      }, []);
      this.setState({ partValue: validatedParts.join(", ") });
    }
  }

  onAddClick() {
    // TODO: Validate to ensure all fields are entered correctly.
    // How to add constraints...
    if (this.state.constraintType === ConstraintTypes.Relative && this.state.object) {
      // subjectTarget, object, objectTarget, comparison, offset
      this.props.onAddRelativeConstraint(
        this.state.ids,
        this.state.target,
        this.state.object,
        this.state.objectTarget,
        this.state.comparison,
        this.state.offset
      );
    }
    if (this.state.constraintType === ConstraintTypes.Date && this.state.date) {
      // subjectTarget, comparison, date
      this.props.onAddDateConstraint(this.state.ids, this.state.target, this.state.comparison, this.state.date);
    }
    if (this.state.constraintType === ConstraintTypes.DatePart && this.state.partValue) {
      // subjectTarget, partPrecision, partValue
      this.props.onAddDatePartConstraint(
        this.state.ids,
        this.state.target,
        this.state.partPrecision,
        this.state.partValue
      );
    }
    this.props.closePopover();
  }

  render() {
    const isDesktop = preprocessorQuery.isDesktop();
    let typeOptionId = this.state.constraintType;
    if (this.state.constraintType === ConstraintTypes.Date || this.state.constraintType === ConstraintTypes.Relative) {
      if (this.state.comparison) {
        typeOptionId += `-${this.state.comparison}`;
      }
      if (this.state.objectTarget) {
        typeOptionId += `-${this.state.objectTarget}`;
      }
    } else {
      typeOptionId += `-${this.state.partPrecision}`;
    }

    const months = getMonthsFromCalendar(this.props.calendar);
    const weekdays = getWeekdaysFromCalendar(this.props.calendar);

    // TODO: Figure out if constraint values are meant to a single string or allow list
    const monthOptions = months
      .map(month => {
        return { label: month.name, value: month.name };
      })
      .toArray();
    const monthValues = this.state.partValue
      ? monthOptions.filter(month => this.state.partValue.split(", ").includes(month.value))
      : [];
    const monthValuesList = this.state.partValue ? List(this.state.partValue.split(", ")) : List();

    const weekdayOptions = weekdays
      .map(weekday => {
        return { label: weekday.name, value: weekday.name };
      })
      .toArray();
    const weekdayValues = this.state.partValue
      ? weekdayOptions.filter(weekday => this.state.partValue.split(", ").includes(weekday.value))
      : [];
    const weekdayValuesList = this.state.partValue ? List(this.state.partValue.split(", ")) : List();

    return (
      <div className="inspectorDateConstraintOptionsView" id="dateConstraintView">
        <InspectorRow>
          <InspectorField style={{ flexGrow: 1 }}>
            <FormDropdown
              value={this.state.target}
              options={targetOptions}
              onChange={this.onTargetChange}
              onToggleMenu={this.props.onChildPopover}
              title={this.props.title}
              backText={this.props.backText}
            />
          </InspectorField>
          <InspectorField style={{ flexGrow: 2 }}>
            <FormDropdown
              value={typeOptionId}
              options={getComparisonOptions(this.props.calendar, this.props.constraintDependencyOptions)}
              onChange={this.onTypeChange}
              onToggleMenu={this.props.onChildPopover}
              title={this.props.title}
              backText={this.props.backText}
            />
          </InspectorField>
        </InspectorRow>
        {this.state.constraintType === ConstraintTypes.Date ? (
          <InspectorRow>
            <InspectorField>
              <SingleFormDatePicker
                value={this.state.date}
                calendar={this.props.calendar}
                onChange={this.onDateChange}
                onToggleCalendarPicker={this.props.onChildPopover}
                title={this.props.title}
                backText={this.props.backText}
                targetId="dateConstraintView"
                useBackdrop={isDesktop}
              />
            </InspectorField>
          </InspectorRow>
        ) : null}
        {this.state.constraintType === ConstraintTypes.Relative ? (
          <InspectorRow label="Item">
            <InspectorField>
              <FormDropdown
                options={this.props.constraintDependencyOptions.toArray()}
                allowSearch={true}
                value={this.state.object}
                onChange={this.onObjectChange}
                onToggleMenu={this.props.onChildPopover}
                title={this.props.title}
                backText={this.props.backText}
              />
            </InspectorField>
          </InspectorRow>
        ) : null}
        {this.state.constraintType === ConstraintTypes.Relative ? (
          <InspectorRow label="Offset">
            <InspectorField>
              <FormDurationPicker
                value={this.state.offset}
                calendar={this.props.calendar}
                onChange={this.onOffsetChange}
              />
            </InspectorField>
          </InspectorRow>
        ) : null}
        {this.state.constraintType === ConstraintTypes.DatePart ? (
          <InspectorRow>
            <InspectorField>
              {this.state.partPrecision === Precision.Month ? (
                <FormList
                  id="dateConstraintDatePartMonth"
                  values={monthValues}
                  options={monthOptions}
                  emptyLabel={"Click to add first item"}
                  canDelete={true}
                  canAdd={true}
                  allowSearch={true}
                  onAddItem={optionId => this.onPartValueChange(monthValuesList.push(optionId))}
                  onDeleteItem={optionId => this.onPartValueChange(monthValuesList.filter(t => t !== optionId))}
                />
              ) : this.state.partPrecision === Precision.Week ? (
                <FormList
                  id="dateConstraintDatePartWeek"
                  values={weekdayValues}
                  options={weekdayOptions}
                  emptyLabel={"Click to add first item"}
                  canDelete={true}
                  canAdd={true}
                  allowSearch={true}
                  onAddItem={optionId => this.onPartValueChange(weekdayValuesList.push(optionId))}
                  onDeleteItem={optionId => this.onPartValueChange(weekdayValuesList.filter(t => t !== optionId))}
                />
              ) : (
                <FormTextField
                  value={this.state.partValue}
                  numeric={false}
                  onChange={this.onPartValueChange}
                  placeholder="Comma separated list"
                />
              )}
            </InspectorField>
          </InspectorRow>
        ) : null}
        <InspectorRow>
          <InspectorField className="rightAligned">
            <FormButton label={this.state.ids ? "Done" : "Add"} primary={true} onClick={this.onAddClick} />
          </InspectorField>
        </InspectorRow>
      </div>
    );
  }
}

InspectorEditDateConstraintView.propTypes = {
  calendar: PropTypeShapes.calendar.isRequired,
  constraintDependencyOptions: PropTypeShapes.listOptionList.isRequired,
  onAddDateConstraint: PropTypes.func.isRequired,
  onAddRelativeConstraint: PropTypes.func.isRequired,
  onAddDatePartConstraint: PropTypes.func.isRequired,
  closePopover: PropTypes.func.isRequired,
  onChildPopover: PropTypes.func.isRequired,
  title: PropTypes.string,
  backText: PropTypes.string,
  constraint: PropTypeShapes.inspectorConstraint,
  repositionPopover: PropTypes.func
};

export default withPopoverRepositionContext(InspectorEditDateConstraintView);
